<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateContractRevision',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const formName = ref('contract');
  const formId = ref(null);
  const labelAlign = ref('right');
  const item = ref(null);
  const statuses = ref(null);
  const options = ref({
    copy_services: true,
    copy_service_fees: true,
    skip_outdated_services: true,
    skip_outdated_service_fees: true,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    formName: formName.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    item: item.value,
    statuses: statuses.value,
    options: options.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return {
      id: props.contract.id,
      base_revision_id: props.contract.revision_id,
      revision_number: props.contract.revision_number + 1,
      revision_date: $fn.value.today(),
      valid_from: props.contract.lifecycle.valid_to
        ? $fn.value.dateAdd(props.contract.lifecycle.valid_to, { days: 1 })
        : $fn.value.today(),
      valid_to: null,
      status: 'draft',
    };
  };
  const $$load_statuses = async () => {
    return $fn.value.getEnumValuesFromSchema('ContractStatus');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formName' },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'item' },
      { name: 'statuses' },
      {
        name: 'options',
        cache: {
          type: 'local',
          key: 'CreateContractRevision.createRevisionOptions',
        },
      },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      statuses: $$load_statuses,
    },
    ux: {},
    dataRefs: {
      formName,
      formId,
      labelAlign,
      item,
      statuses,
      options,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Create Contract Revision') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_blmazqw7y_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2140198529698299_reloadSlickgrid_ck0abamu5_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'contracts',
    };
  };
  const act_2140198529698299_closeComponent_vl6mrfhto_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_2140198529698299_navigate_z7qv167og_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.contract',
        params: {
          id: $event.data.response.data.id,
          rev: $event.data.response.data.revision_number,
        },
      },
    };
  };
  const act_2140198529698299_request_xv42lwxg9_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_contract_revision',
      data: { ...item.value, ...options.value },
    };
  };

  const act_7315092382398562_closeModal_blmazqw7y =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_blmazqw7y_getActionArgs,
    });
  const inst_evh_7315092382398562_close_7qlf15614 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_blmazqw7y,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_7qlf15614],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_wmhpkibvy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_d6p0ioyqg =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_wmhpkibvy,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_d6p0ioyqg],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_08kwfy64g =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_ehfhd0mx0 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_08kwfy64g,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_ehfhd0mx0],
    event: 'close',
  });
  const act_2140198529698299_reloadSlickgrid_ck0abamu5 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2140198529698299_reloadSlickgrid_ck0abamu5_getActionArgs,
    });
  const inst_evh_2140198529698299_success_nppgjvzln =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_reloadSlickgrid_ck0abamu5,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_2140198529698299_closeComponent_vl6mrfhto =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2140198529698299_closeComponent_vl6mrfhto_getActionArgs,
    });
  const inst_evh_2140198529698299_success_6teza6o7t =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_vl6mrfhto,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2140198529698299_navigate_z7qv167og =
    new actionTypes.NavigateAction({
      actionArgs: act_2140198529698299_navigate_z7qv167og_getActionArgs,
    });
  const inst_evh_2140198529698299_success_p2bd7ub09 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_navigate_z7qv167og,
      event: 'success',
      displayName: 'navigate',
    });
  const act_2140198529698299_request_xv42lwxg9 = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_xv42lwxg9_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_2140198529698299_success_nppgjvzln,
      inst_evh_2140198529698299_success_6teza6o7t,
      inst_evh_2140198529698299_success_p2bd7ub09,
    ],
  });
  const inst_evh_2140198529698299_clickPreventStop_1eqnurb2z =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_xv42lwxg9,
      event: 'click.prevent.stop',
      displayName: 'save',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_1eqnurb2z],
      event: 'click.prevent.stop',
    });
  const act_2434514588044777_closeModal_o0o1cvhot =
    new actionTypes.CloseModalAction({});
  const inst_evh_2434514588044777_clickPreventStop_fdx1i8fu0 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_o0o1cvhot,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_fdx1i8fu0],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_7qlf15614,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_d6p0ioyqg,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_ehfhd0mx0,
    inst_evh_8564662037462133_close,
    inst_evh_2140198529698299_success_nppgjvzln,
    inst_evh_2140198529698299_success_6teza6o7t,
    inst_evh_2140198529698299_success_p2bd7ub09,
    inst_evh_2140198529698299_clickPreventStop_1eqnurb2z,
    save: inst_evh_2140198529698299_clickPreventStop_1eqnurb2z,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2434514588044777_clickPreventStop_fdx1i8fu0,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_7qlf15614 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_7qlf15614.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_d6p0ioyqg = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_d6p0ioyqg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_ehfhd0mx0 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_ehfhd0mx0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_nppgjvzln = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_nppgjvzln.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_6teza6o7t = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_6teza6o7t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_p2bd7ub09 = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_p2bd7ub09.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_1eqnurb2z = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_1eqnurb2z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_fdx1i8fu0 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_fdx1i8fu0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateContractRevision"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm :id="formId" :name="formName">
        <ItpFormGroup
          name="revision_number"
          :label="pgettext('Contract', 'Revision Number')"
          :label-cols-md="4"
          :content-cols-md="2"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormInput
            type="number"
            name="revision_number"
            v-model="item.revision_number"
            :value="item.revision_number"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.revision_number =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="status"
          :label="pgettext('Contract', 'Status')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormSelect2
            name="status"
            v-model="item.status"
            :value="item.status"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.status =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            valueField="value"
            textField="text"
            :options="statuses"
            required
          >
          </ItpFormSelect2>
        </ItpFormGroup>
        <ItpFormGroup
          name="revision_date"
          :label="pgettext('Contract', 'Revision Date')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormDatetime
            type="date"
            name="revision_date"
            v-model="item.revision_date"
            required
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_from"
          :label="pgettext('Contract', 'Validity')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="row"
          required
        >
          <div class="col">
            <ItpFormDatetime
              name="valid_from"
              v-model="item.valid_from"
              required
            >
            </ItpFormDatetime>
          </div>
          <div class="col">
            <ItpFormDatetime name="valid_to" v-model="item.valid_to" required>
            </ItpFormDatetime>
          </div>
        </ItpFormGroup>
        <hr />
        <ItpAlert variant="info" show>
          <p>
            <ItpIcon fa="info-circle"> </ItpIcon>
            <ItpText wrap>
              {{
                pgettext(
                  'CreateContractRevision',
                  'The new revision will be created based on the opened revision. Select the following options to copy services and/or fees to the new revision.'
                )
              }}
            </ItpText>
          </p>
        </ItpAlert>
        <ItpFormGroup
          name="options"
          :label="pgettext('CreateContractRevision', 'Options')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
        >
          <ItpBox direction="column" class="ps-1">
            <ItpFormCheckbox
              name="copy_services"
              :label="pgettext('CreateContractRevision', 'Copy services')"
              inline
              v-model="options.copy_services"
              :value="options.copy_services"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (options.copy_services =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
            <ItpFormCheckbox
              name="copy_service_fees"
              :label="pgettext('CreateContractRevision', 'Copy service fees')"
              inline
              v-model="options.copy_service_fees"
              :value="options.copy_service_fees"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (options.copy_service_fees =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
            <ItpFormCheckbox
              name="skip_outdated_services"
              :label="
                pgettext('CreateContractRevision', 'Skip outdated services')
              "
              inline
              v-model="options.skip_outdated_services"
              :value="options.skip_outdated_services"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (options.skip_outdated_services =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
            <ItpFormCheckbox
              name="skip_outdated_service_fees"
              :label="
                pgettext('CreateContractRevision', 'Skip outdated service fees')
              "
              inline
              v-model="options.skip_outdated_service_fees"
              :value="options.skip_outdated_service_fees"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (options.skip_outdated_service_fees =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </ItpBox>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :text="pgettext('Contract', 'Create revision')"
          :spinning="actions.save.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          class="ms-3"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          variant="default"
          :text="gettext('Close')"
          :disabled="actions.save.isRunning"
          class="ms-3"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
